import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Slash, Tags, RefreshCw, Search, Wrench, User, Braces, Info, X, Play, Phone, MessageSquare, SlidersHorizontal } from 'lucide-react';
import { isV4 } from '../voice/V4VoiceControls';
import { VOICE_EXPRESSION_GROUPS, voiceExpressionRE, detectAudioTag, audioTagItems } from '../voice/voiceExpressions';

// ─── Full-size Global Prompt editor with color-coded tokens, "/" command
//     palette and a Fields & Values dropdown (custom fields, custom values,
//     standard variables). Plain text underneath — saving is unchanged. ───

const VAR_RE = /\{\{\s*([a-zA-Z0-9_.\-]+)\s*\}\}/g;

const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Mirror-div caret coordinates for a textarea
const getCaretXY = (ta, pos) => {
  const div = document.createElement('div');
  const style = window.getComputedStyle(ta);
  ['fontFamily', 'fontSize', 'fontWeight', 'lineHeight', 'letterSpacing', 'paddingTop', 'paddingLeft', 'paddingRight', 'paddingBottom', 'borderWidth', 'boxSizing'].forEach(p => { div.style[p] = style[p]; });
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.wordBreak = 'break-word';
  div.style.width = `${ta.clientWidth}px`;
  div.textContent = ta.value.substring(0, pos);
  const span = document.createElement('span');
  span.textContent = ta.value.substring(pos) || '.';
  div.appendChild(span);
  document.body.appendChild(div);
  const xy = { top: span.offsetTop, left: span.offsetLeft };
  document.body.removeChild(div);
  return xy;
};

export const PromptEditor = ({
  value, onChange, tools = [], fields = { standard: [], custom: [] }, customValues = [],
  ghlConnected = false, onResync, lastSynced, onTest,
  styleGuardrails = '', responseGuidelines = '', onChangeGuardrails, onChangeGuidelines,
  chatPrompt = '', onChangeChatPrompt, chatPromptEnabled = false,
  voiceModel = null,
  customVoiceCues = [], onChangeVoiceCues, firstTasks = {},
  isDarkMode, cardBg, cardBorder, inputBg, textPrimary, textSecondary,
}) => {
  const taRef = useRef(null);
  const overlayRef = useRef(null);
  const wrapRef = useRef(null);
  const rootRef = useRef(null);
  const ggRef = useRef(null);
  const glRef = useRef(null);
  const ggOvRef = useRef(null);
  const glOvRef = useRef(null);
  const [view, setView] = useState('prompt'); // 'prompt' | 'chat' | 'guidelines'
  const [activeTarget, setActiveTarget] = useState('main'); // 'main' | 'guardrails' | 'guidelines'
  const [palette, setPalette] = useState(null); // {query, start, top, left, target}
  const [palIdx, setPalIdx] = useState(0);
  const [palCat, setPalCat] = useState('all');
  const [fvOpen, setFvOpen] = useState(false);
  const [fvTab, setFvTab] = useState('standard');
  const [fvSearch, setFvSearch] = useState('');
  const [resyncing, setResyncing] = useState(false);

  const toolMap = useMemo(() => Object.fromEntries(tools.map(t => [t.id, t])), [tools]);

  // ── Chat prompt: only editable/active when the "Separate Chat Prompt"
  // toggle (Chat settings) is ON — turning it off keeps the text saved ──
  const isChat = view === 'chat';
  const showExpressions = isV4(voiceModel) && view === 'prompt';
  const expressionRE = useMemo(() => voiceExpressionRE(customVoiceCues), [customVoiceCues]);
  const activeValue = isChat ? (chatPrompt || '') : (value || '');
  const activeOnChange = useCallback((v) => {
    if (isChat) { onChangeChatPrompt && onChangeChatPrompt(v); }
    else { onChange(v); }
  }, [isChat, onChange, onChangeChatPrompt]);

  useEffect(() => {
    if (!chatPromptEnabled && view === 'chat') setView('prompt');
  }, [chatPromptEnabled, view]);
  useEffect(() => {
    setActiveTarget(view === 'guidelines' ? 'guardrails' : 'main');
  }, [view]);
  useEffect(() => { setPalette(null); setPalCat('all'); }, [view, voiceModel]);

  const fieldKeys = useMemo(() => new Set([
    ...(fields.standard || []).map(f => f.key),
    ...(fields.custom || []).map(f => f.key),
    'knowledge_base',
  ]), [fields]);
  const cvKeys = useMemo(() => new Set((customValues || []).map(v => v.key)), [customValues]);

  // ── Token colors (background only — never changes text metrics) ──
  const cls = {
    toolOn: isDarkMode ? 'color:#c4b5fd;background:rgba(167,139,250,0.16);border-radius:3px' : 'color:#7c3aed;background:rgba(139,92,246,0.18);border-radius:3px',
    varOk: isDarkMode ? 'color:#5eead4;background:rgba(45,212,191,0.14);border-radius:3px' : 'color:#0d9488;background:rgba(20,184,166,0.20);border-radius:3px',
    cvOk: isDarkMode ? 'color:#7dd3fc;background:rgba(56,189,248,0.14);border-radius:3px' : 'color:#0284c7;background:rgba(14,165,233,0.18);border-radius:3px',
    bad: isDarkMode ? 'color:#f87171;background:rgba(248,113,113,0.15);border-radius:3px' : 'color:#dc2626;background:rgba(239,68,68,0.16);border-radius:3px',
    unknown: isDarkMode ? 'color:#9ca3af;background:rgba(255,255,255,0.08);border-radius:3px' : 'color:#52525b;background:rgba(0,0,0,0.08);border-radius:3px',
  };

  const toolRe = useMemo(() => {
    const ids = tools.map(t => t.id).sort((a, b) => b.length - a.length);
    return ids.length ? new RegExp(`\\b(${ids.join('|')})\\b`, 'g') : null;
  }, [tools]);

  const buildHighlight = useCallback((rawText) => {
    const text = rawText || '';
    const renderExpressions = (seg) => {
      const escaped = esc(seg);
      return showExpressions ? escaped.replace(expressionRE,
        match => `<span style="color:${isDarkMode ? '#fbbf24' : '#92400e'};background:${isDarkMode ? 'rgba(251,191,36,.12)' : '#fef3c7'};border-radius:3px">${match}</span>`) : escaped;
    };
    const renderPlain = (seg) => {
      if (!toolRe) return renderExpressions(seg);
      let out = '', li = 0, m;
      toolRe.lastIndex = 0;
      while ((m = toolRe.exec(seg)) !== null) {
        out += renderExpressions(seg.slice(li, m.index));
        const t = toolMap[m[1]];
        out += `<span style="${t?.enabled ? cls.toolOn : cls.bad}">${esc(m[0])}</span>`;
        li = m.index + m[0].length;
      }
      return out + renderExpressions(seg.slice(li));
    };
    let html = '', last = 0, m;
    VAR_RE.lastIndex = 0;
    while ((m = VAR_RE.exec(text)) !== null) {
      html += renderPlain(text.slice(last, m.index));
      const name = m[1];
      let style;
      if (name === 'knowledge_base') style = cls.varOk;
      else if (name in firstTasks) style = (firstTasks[name] || '').trim() ? cls.varOk : cls.unknown;
      else if (!ghlConnected) style = cls.unknown;
      else if (name.startsWith('custom_values.')) style = cvKeys.has(name) ? cls.cvOk : cls.bad;
      else if (name.startsWith('contact.')) style = fieldKeys.has(name) ? cls.varOk : cls.bad;
      else style = fieldKeys.has(`contact.${name}`) || fieldKeys.has(name) ? cls.varOk : cls.unknown;
      html += `<span style="${style}">${esc(m[0])}</span>`;
      last = m.index + m[0].length;
    }
    html += renderPlain(text.slice(last));
    return html + '<br/>';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolRe, toolMap, fieldKeys, cvKeys, ghlConnected, isDarkMode, showExpressions, expressionRE, firstTasks]);

  const highlighted = useMemo(() => buildHighlight(activeValue), [buildHighlight, activeValue]);
  const ggHighlighted = useMemo(() => buildHighlight(styleGuardrails), [buildHighlight, styleGuardrails]);
  const glHighlighted = useMemo(() => buildHighlight(responseGuidelines), [buildHighlight, responseGuidelines]);

  const syncScroll = () => {
    if (overlayRef.current && taRef.current) {
      overlayRef.current.scrollTop = taRef.current.scrollTop;
      overlayRef.current.scrollLeft = taRef.current.scrollLeft;
    }
  };
  const syncAuxScroll = (taR, ovR) => () => {
    if (ovR.current && taR.current) {
      ovR.current.scrollTop = taR.current.scrollTop;
      ovR.current.scrollLeft = taR.current.scrollLeft;
    }
  };

  // ── Insertion targets: main editor + the two guidelines boxes ──
  const targetFor = (key) => key === 'guardrails'
    ? { ref: ggRef, value: styleGuardrails || '', onChange: (v) => onChangeGuardrails && onChangeGuardrails(v) }
    : key === 'guidelines'
      ? { ref: glRef, value: responseGuidelines || '', onChange: (v) => onChangeGuidelines && onChangeGuidelines(v) }
      : { ref: taRef, value: activeValue, onChange: activeOnChange };

  // ── Insert text at caret (replacing [from, to) if given) ──
  const insertAt = useCallback((snippet, from = null, to = null, targetKey = null) => {
    const t = targetFor(targetKey || activeTarget);
    const ta = t.ref.current;
    const text = t.value;
    const s = from !== null ? from : (ta ? ta.selectionStart : text.length);
    const e = to !== null ? to : (ta ? ta.selectionEnd : text.length);
    const next = text.slice(0, s) + snippet + text.slice(e);
    t.onChange(next);
    requestAnimationFrame(() => {
      if (ta) {
        ta.focus();
        const p = s + snippet.length;
        ta.setSelectionRange(p, p);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeValue, activeOnChange, activeTarget, styleGuardrails, responseGuidelines, onChangeGuardrails, onChangeGuidelines]);

  // ── Slash palette detection ──
  const detectSlash = (text, caret) => {
    const upto = text.slice(0, caret);
    const m = upto.match(/(?:^|[\s([>])\/([a-zA-Z0-9_]*)$/);
    if (!m) return null;
    return { mode: 'slash', query: m[1], start: caret - m[1].length - 1 };
  };

  // Position the palette relative to the component root so it works in every view
  const placePalette = (ta, slash, targetKey) => {
    const xy = getCaretXY(ta, slash.start);
    const rootRect = rootRef.current?.getBoundingClientRect();
    const taRect = ta.getBoundingClientRect();
    // Rects include the app's CSS zoom; caret offsets and absolute coordinates do not.
    const scale = rootRect?.width / rootRef.current?.offsetWidth || 1;
    const baseTop = (taRect.top - (rootRect?.top || 0)) / scale;
    const baseLeft = (taRect.left - (rootRect?.left || 0)) / scale;
    let top = baseTop + xy.top - ta.scrollTop + 26;
    const rootH = rootRef.current?.clientHeight || 400;
    const maxHeight = Math.max(80, Math.min(360, rootH - 16, (window.innerHeight - (rootRect?.top || 0)) / scale - 16));
    if (top + maxHeight > rootH) top = Math.max(8, baseTop + xy.top - ta.scrollTop - maxHeight);
    const left = Math.min(baseLeft + xy.left, (rootRef.current?.clientWidth || 600) - 550);
    if (palette?.mode !== slash.mode) setPalCat('all');
    setPalette({ ...slash, target: targetKey, top: Math.max(8, Math.min(top, rootH - maxHeight - 8)), left: Math.max(8, left), maxHeight });
    setPalIdx(0);
  };

  const handleChange = (e) => {
    const text = e.target.value;
    activeOnChange(text);
    const caret = e.target.selectionStart;
    const slash = (showExpressions && detectAudioTag(text, caret)) || detectSlash(text, caret);
    if (slash) placePalette(e.target, slash, 'main');
    else setPalette(null);
  };

  // Guidelines textareas share the same slash-command experience
  const handleAuxChange = (key) => (e) => {
    const text = e.target.value;
    targetFor(key).onChange(text);
    const slash = detectSlash(text, e.target.selectionStart);
    if (slash) placePalette(e.target, slash, key);
    else setPalette(null);
  };

  // ── Palette items ──
  const paletteItems = useMemo(() => {
    if (!palette) return [];
    if (palette.mode === 'audio') return audioTagItems(palette.query, palCat, customVoiceCues);
    const q = palette.query.toLowerCase();
    const match = (s) => !q || s.toLowerCase().includes(q);
    const items = [];
    if ((palCat === 'all' || palCat === 'tools') && ('insert tools section'.includes(q) || 'tools'.includes(q))) {
      const on = tools.filter(t => t.enabled);
      if (on.length) {
        const line = (t) => {
          const d = (t.description || '').trim();
          const lc = d ? d.charAt(0).toLowerCase() + d.slice(1) : 'perform this action';
          return `- Use ${t.id} to ${lc}${/[.!?]$/.test(lc) ? '' : '.'}`;
        };
        items.push({
          kind: 'block', id: '__tools_section', label: 'Insert Tools Section',
          desc: `Adds "## Tools" listing all ${on.length} enabled tools with descriptions`,
          insert: `## Tools\n${on.map(line).join('\n')}\n`,
        });
      }
    }
    if (palCat === 'all' || palCat === 'first_task') {
      [['inbound_first_task', 'Inbound First Task', 'What the AI does first when someone calls in'],
        ['outbound_first_task', 'Outbound First Task', 'What the AI does first when it calls out']]
        .filter(([k, n]) => match(k) || match(n)).forEach(([k, n, d]) => items.push({
          kind: 'standard', id: k, label: n, desc: `{{${k}}} — ${d}`, insert: `{{${k}}}`,
        }));
    }
    if (palCat === 'all' || palCat === 'tools') {
      const sorted = [...tools].sort((a, b) => Number(b.enabled) - Number(a.enabled));
      sorted.filter(t => match(t.id)).forEach(t => items.push({
        kind: 'tool', id: t.id, label: t.id, desc: t.description, enabled: t.enabled, insert: t.id,
      }));
    }
    if (palCat === 'all' || palCat === 'standard') {
      (fields.standard || []).filter(f => match(f.key) || match(f.name)).forEach(f => items.push({
        kind: 'standard', id: f.key, label: f.name, desc: `{{${f.key}}}`, insert: `{{${f.key}}}`,
      }));
    }
    if (palCat === 'all' || palCat === 'custom') {
      (fields.custom || []).filter(f => match(f.key) || match(f.name)).forEach(f => items.push({
        kind: 'custom', id: f.key, label: f.name, desc: `{{${f.key}}}`, insert: `{{${f.key}}}`,
      }));
    }
    if (palCat === 'all' || palCat === 'values') {
      (customValues || []).filter(v => match(v.key) || match(v.name)).forEach(v => items.push({
        kind: 'value', id: v.key, label: v.name, desc: `{{${v.key}}}`, insert: `{{${v.key}}}`,
      }));
    }
    return items.slice(0, 40);
  }, [palette, palCat, tools, fields, customValues, customVoiceCues]);

  const pickItem = (item) => {
    const tKey = (palette && palette.target) || 'main';
    const t = targetFor(tKey);
    const ta = t.ref.current;
    let caret = ta ? ta.selectionStart : t.value.length;
    if (palette.mode === 'audio') {
      // Completing an existing bracketed cue replaces its suffix/closing bracket too.
      const suffix = t.value.slice(caret).match(/^[^\[\]\n\r]*\]/);
      if (suffix) caret += suffix[0].length;
      if (item.register && onChangeVoiceCues && customVoiceCues.length < 200) {
        onChangeVoiceCues([...customVoiceCues, item.cue]);
      }
    }
    const space = /\s/.test(t.value.charAt(caret)) ? '' : ' ';
    insertAt(item.kind === 'block' ? item.insert : `${item.insert}${space}`, palette.start, caret, tKey);
    setPalette(null);
  };

  const handleKeyDown = (e) => {
    if (!palette) return;
    if (e.isComposing || e.nativeEvent?.isComposing) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setPalIdx(i => Math.max(0, Math.min(i + 1, paletteItems.length - 1))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setPalIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' || e.key === 'Tab') {
      if (paletteItems[palIdx]) { e.preventDefault(); pickItem(paletteItems[palIdx]); }
    }
    else if (e.key === 'Escape') { e.preventDefault(); setPalette(null); }
  };

  useEffect(() => { setPalIdx(0); }, [paletteItems.length]);
  useEffect(() => {
    rootRef.current?.querySelector('[data-palette-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [palIdx]);

  const kindBadge = (item) => {
    const badge = (dark, light, label) => <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold shrink-0 ${isDarkMode ? dark : light}`}>{label}</span>;
    if (item.kind === 'block') return badge('bg-amber-400/15 text-amber-400', 'bg-amber-100 text-amber-700', 'section');
    if (item.kind === 'expression') return <span data-testid={`audio-tag-status-${item.id}`} title={item.status === 'Documented' ? 'ElevenLabs example; results vary by voice' : 'Audition with your chosen voice'}>{badge('bg-amber-400/15 text-amber-400', 'bg-amber-100 text-amber-700', item.status)}</span>;
    if (item.kind === 'tool') return item.enabled
      ? badge('bg-violet-400/15 text-violet-400', 'bg-violet-100 text-violet-700', 'tool')
      : badge('bg-red-400/15 text-red-400', 'bg-red-100 text-red-600', 'tool · off');
    if (item.kind === 'value') return badge('bg-sky-400/15 text-sky-400', 'bg-sky-100 text-sky-700', 'custom value');
    if (item.kind === 'custom') return badge('bg-teal-400/15 text-teal-400', 'bg-teal-100 text-teal-700', 'custom field');
    return badge('bg-teal-400/15 text-teal-400', 'bg-teal-100 text-teal-700', 'standard');
  };

  // ── Fields & Values dropdown rows ──
  const fvRows = useMemo(() => {
    const q = fvSearch.toLowerCase();
    const match = (f) => !q || f.key.toLowerCase().includes(q) || (f.name || '').toLowerCase().includes(q);
    if (fvTab === 'standard') return (fields.standard || []).filter(match);
    if (fvTab === 'custom') return (fields.custom || []).filter(match);
    return (customValues || []).filter(v => !q || v.key.toLowerCase().includes(q) || (v.name || '').toLowerCase().includes(q));
  }, [fvTab, fvSearch, fields, customValues]);

  const handleResync = async () => {
    if (!onResync) return;
    setResyncing(true);
    try { await onResync(); } finally { setResyncing(false); }
  };

  const editorFont = 'font-mono text-[13px] leading-6';

  return (
    <div ref={rootRef} className={`flex flex-col h-full rounded-2xl border ${cardBorder} ${cardBg} overflow-hidden relative`} data-testid="prompt-editor">
      {/* Toolbar */}
      <div className={`shrink-0 flex flex-wrap items-center gap-3 px-4 py-2.5 border-b ${cardBorder}`}>
        <h3 className={`text-sm font-semibold ${textPrimary}`} data-testid="prompt-editor-title">
          {view === 'guidelines' ? 'Guidelines' : chatPromptEnabled ? (isChat ? 'Chat Prompt' : 'Voice Prompt') : 'System Prompt'}
        </h3>
        <span className={`text-[11px] ${textSecondary}`} data-testid="prompt-char-count">
          {(view === 'guidelines' ? (styleGuardrails.length + responseGuidelines.length) : activeValue.length).toLocaleString()} characters
        </span>
        <div className={`flex flex-wrap items-center gap-2 text-[11px] ${textSecondary}`} data-testid="prompt-shortcut-hints">
          <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border ${cardBorder}`} data-testid="prompt-command-hint">
            <Slash size={10} aria-hidden="true" /> for commands
          </span>
          {showExpressions && <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border ${cardBorder}`} data-testid="prompt-expression-hint">
            <span className="font-mono" aria-hidden="true">[</span> for expressions
          </span>}
        </div>
        <div className="flex-1" />
        {chatPromptEnabled && (
          <>
            <button
              onClick={() => setView('prompt')}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${view === 'prompt' ? (isDarkMode ? 'bg-teal-400/10 text-teal-400 border-teal-400/30' : 'bg-teal-50 text-teal-700 border-teal-300') : `${inputBg} ${cardBorder} ${textPrimary} hover:border-teal-400/40`}`}
              data-testid="prompt-mode-prompt"
            >
              <Phone size={12} /> Voice Prompt
            </button>
            <button
              onClick={() => setView('chat')}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${view === 'chat' ? (isDarkMode ? 'bg-teal-400/10 text-teal-400 border-teal-400/30' : 'bg-teal-50 text-teal-700 border-teal-300') : `${inputBg} ${cardBorder} ${textPrimary} hover:border-teal-400/40`}`}
              data-testid="prompt-mode-chat"
            >
              <MessageSquare size={12} /> Chat Prompt
            </button>
          </>
        )}
        <button
          onClick={() => setView(v => v === 'guidelines' ? 'prompt' : 'guidelines')}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${view === 'guidelines' ? (isDarkMode ? 'bg-teal-400/10 text-teal-400 border-teal-400/30' : 'bg-teal-50 text-teal-700 border-teal-300') : `${inputBg} ${cardBorder} ${textPrimary} hover:border-violet-400/50`}`}
          data-testid="prompt-mode-guidelines"
        >
          <SlidersHorizontal size={12} /> Guidelines
        </button>
        <div className="relative">
          <button
            onClick={() => setFvOpen(o => !o)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${fvOpen ? (isDarkMode ? 'bg-teal-400/10 text-teal-400 border-teal-400/30' : 'bg-teal-50 text-teal-700 border-teal-300') : `${inputBg} ${cardBorder} ${textPrimary} hover:border-teal-400/40`}`}
            data-testid="fields-values-btn"
          >
            <Tags size={12} /> Fields &amp; Values
          </button>
          {fvOpen && (
            <div className={`absolute right-0 top-full mt-2 w-[min(400px,calc(100vw-100px))] rounded-2xl border ${cardBorder} ${isDarkMode ? 'bg-[#101117]' : 'bg-white'} shadow-2xl z-50 flex flex-col`} data-testid="fields-values-panel">
              <div className={`flex items-center justify-between px-4 pt-3 pb-2`}>
                <p className={`text-sm font-semibold ${textPrimary}`}>Custom Fields &amp; Values</p>
                <button data-testid="fields-values-close" aria-label="Close fields" onClick={() => setFvOpen(false)} className={`p-1 rounded ${textSecondary} ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}><X size={13} /></button>
              </div>
              <div className="flex items-center gap-1 px-4 pb-2">
                {[['standard', 'Standard'], ['custom', 'Custom Fields'], ['values', 'Custom Values']].map(([id, label]) => (
                  <button key={id} onClick={() => setFvTab(id)}
                    className={`text-[11px] px-2.5 py-1 rounded-lg font-medium transition-colors ${fvTab === id ? (isDarkMode ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30' : 'bg-teal-50 text-teal-700 border border-teal-300') : `${textSecondary} ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'} border border-transparent`}`}
                    data-testid={`fv-tab-${id}`}
                  >{label}</button>
                ))}
              </div>
              <div className="px-4 pb-2">
                <div className="relative">
                  <Search size={12} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${textSecondary}`} />
                  <input value={fvSearch} onChange={(e) => setFvSearch(e.target.value)} placeholder="Search…"
                    className={`w-full pl-8 pr-3 py-1.5 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs focus:outline-none focus:border-teal-400/50`}
                    data-testid="fv-search" />
                </div>
              </div>
              <div className="max-h-[300px] overflow-y-auto px-3 pb-2 space-y-1">
                {!ghlConnected && fvTab !== 'standard' && (
                  <p className={`text-[11px] ${textSecondary} px-2 py-3 text-center`}>Connect your CRM in Settings to sync {fvTab === 'custom' ? 'custom fields' : 'custom values'}.</p>
                )}
                {fvRows.length === 0 && (ghlConnected || fvTab === 'standard') && (
                  <p className={`text-[11px] ${textSecondary} px-2 py-3 text-center`}>Nothing found{fvSearch ? ` for "${fvSearch}"` : ' — try Resync'}.</p>
                )}
                {fvRows.map((f) => (
                  <div key={f.key} className={`flex items-center gap-2 px-2.5 py-2 rounded-xl ${inputBg} border ${cardBorder}`} data-testid={`fv-row-${f.key}`}>
                    <Braces size={12} className={fvTab === 'values' ? (isDarkMode ? 'text-sky-400 shrink-0' : 'text-sky-600 shrink-0') : (isDarkMode ? 'text-teal-400 shrink-0' : 'text-teal-600 shrink-0')} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs font-medium ${textPrimary} truncate`}>{f.name || f.key}</p>
                      <p className={`text-[10px] font-mono ${textSecondary} truncate`}>{`{{${f.key}}}`}</p>
                    </div>
                    <button
                      onClick={() => insertAt(`{{${f.key}}}`)}
                      className={`text-[11px] px-2.5 py-1 rounded-lg border font-medium shrink-0 ${isDarkMode ? 'bg-teal-400/10 text-teal-400 border-teal-400/30 hover:bg-teal-400/20' : 'bg-teal-50 text-teal-700 border-teal-300 hover:bg-teal-100'}`}
                      data-testid={`fv-add-${f.key}`}
                    >Add</button>
                  </div>
                ))}
              </div>
              <div className={`flex items-center justify-between px-4 py-2.5 border-t ${cardBorder}`}>
                <span className={`text-[10px] ${textSecondary}`}>{lastSynced ? `Synced ${new Date(lastSynced).toLocaleString()}` : (ghlConnected ? 'Not synced yet' : 'CRM not connected')}</span>
                <button
                  onClick={handleResync}
                  disabled={resyncing || !ghlConnected}
                  className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg font-medium ${ghlConnected ? 'bg-violet-500 text-white hover:bg-violet-400' : `${inputBg} ${textSecondary} cursor-not-allowed`}`}
                  data-testid="fv-resync-btn"
                >
                  <RefreshCw size={11} className={resyncing ? 'animate-spin' : ''} /> Resync
                </button>
              </div>
            </div>
          )}
        </div>
        {onTest && (
          <button
            onClick={onTest}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${isDarkMode ? 'bg-cyan-400/10 text-cyan-400 border-cyan-400/30 hover:bg-cyan-400/20' : 'bg-cyan-50 text-cyan-700 border-cyan-300 hover:bg-cyan-100'}`}
            data-testid="test-agent-btn"
          >
            <Play size={12} /> Test Agent
          </button>
        )}
      </div>

      {/* Editor body — overlay + transparent textarea */}
      {view !== 'guidelines' ? (
      <div ref={wrapRef} className="relative flex-1 min-h-0">
        <div
          ref={overlayRef}
          aria-hidden
          className={`absolute inset-0 overflow-hidden whitespace-pre-wrap break-words px-5 py-4 pointer-events-none ${editorFont} ${textPrimary}`}
          dangerouslySetInnerHTML={{ __html: highlighted }}
        />
        <textarea
          ref={taRef}
          value={activeValue}
          onChange={handleChange}
          onScroll={syncScroll}
          onKeyDown={handleKeyDown}
          aria-label={isChat ? 'Chat prompt' : 'Voice prompt'}
          aria-autocomplete="list"
          aria-controls={palette ? 'prompt-command-list' : undefined}
          aria-activedescendant={palette && paletteItems[palIdx] ? `prompt-option-${paletteItems[palIdx].id}` : undefined}
          onFocus={() => setActiveTarget('main')}
          onBlur={() => setPalette(null)}
          spellCheck={false}
          className={`prompt-ta absolute inset-0 w-full h-full resize-none bg-transparent px-5 py-4 focus:outline-none whitespace-pre-wrap break-words ${editorFont}`}
          style={{ color: 'transparent', caretColor: isDarkMode ? '#ffffff' : '#111827' }}
          placeholder={isChat ? '# Chat identity\nHow this agent texts…\n\nType / for tools & variables' : '# Identity\nYou are…\n\nType / for tools & variables'}
          data-testid={isChat ? 'chat-prompt-textarea' : 'global-prompt-textarea'}
        />
      </div>
      ) : (
      <div className="flex-1 min-h-0 overflow-y-auto p-5 flex flex-col gap-5" data-testid="guidelines-view">
        <div className="flex flex-col flex-1 min-h-[180px]">
          <label className={`text-sm font-semibold ${textPrimary}`}>Style Guardrails</label>
          <p className={`text-xs ${textSecondary} mt-0.5 mb-2`}>How the agent should sound — tone, brevity, conversational style.</p>
          <div className={`relative flex-1 min-h-0 rounded-xl ${inputBg} border ${cardBorder} focus-within:border-teal-400/50 overflow-hidden`}>
            <div
              ref={ggOvRef}
              aria-hidden
              className={`absolute inset-0 overflow-hidden whitespace-pre-wrap break-words px-4 py-3 pointer-events-none text-sm leading-6 ${textPrimary}`}
              dangerouslySetInnerHTML={{ __html: ggHighlighted }}
            />
            <textarea
              ref={ggRef}
              value={styleGuardrails}
              onChange={handleAuxChange('guardrails')}
              onScroll={syncAuxScroll(ggRef, ggOvRef)}
              onKeyDown={handleKeyDown}
              onFocus={() => setActiveTarget('guardrails')}
              onBlur={() => setPalette(null)}
              spellCheck={false}
              className="prompt-ta absolute inset-0 w-full h-full resize-none bg-transparent px-4 py-3 focus:outline-none whitespace-pre-wrap break-words text-sm leading-6"
              style={{ color: 'transparent', caretColor: isDarkMode ? '#ffffff' : '#111827' }}
              placeholder="Be Concise: Respond succinctly, addressing one topic at most.&#10;Be Conversational: Use everyday language.&#10;Be Proactive: Lead the conversation."
              data-testid="style-guardrails-textarea"
            />
          </div>
        </div>
        <div className="flex flex-col flex-1 min-h-[180px]">
          <label className={`text-sm font-semibold ${textPrimary}`}>Response Guidelines</label>
          <p className={`text-xs ${textSecondary} mt-0.5 mb-2`}>How the agent should handle responses — interpretation, staying in character.</p>
          <div className={`relative flex-1 min-h-0 rounded-xl ${inputBg} border ${cardBorder} focus-within:border-teal-400/50 overflow-hidden`}>
            <div
              ref={glOvRef}
              aria-hidden
              className={`absolute inset-0 overflow-hidden whitespace-pre-wrap break-words px-4 py-3 pointer-events-none text-sm leading-6 ${textPrimary}`}
              dangerouslySetInnerHTML={{ __html: glHighlighted }}
            />
            <textarea
              ref={glRef}
              value={responseGuidelines}
              onChange={handleAuxChange('guidelines')}
              onScroll={syncAuxScroll(glRef, glOvRef)}
              onKeyDown={handleKeyDown}
              onFocus={() => setActiveTarget('guidelines')}
              onBlur={() => setPalette(null)}
              spellCheck={false}
              className="prompt-ta absolute inset-0 w-full h-full resize-none bg-transparent px-4 py-3 focus:outline-none whitespace-pre-wrap break-words text-sm leading-6"
              style={{ color: 'transparent', caretColor: isDarkMode ? '#ffffff' : '#111827' }}
              placeholder="Adapt and Guess: Try to understand transcripts that may contain errors.&#10;Stay in Character: Keep conversations within your role's scope."
              data-testid="response-guidelines-textarea"
            />
          </div>
        </div>
      </div>
      )}

      {/* Separate trigger domains; identical caret and keyboard behavior. */}
      {palette && (
        <div
          className={`absolute rounded-xl border ${cardBorder} ${isDarkMode ? 'bg-[#101117]' : 'bg-white'} shadow-2xl z-50 overflow-hidden flex flex-col`}
          style={{ top: palette.top, left: palette.left, width: 'min(540px, calc(100% - 16px))', maxHeight: palette.maxHeight }}
          data-testid={palette.mode === 'audio' ? 'audio-tag-palette' : 'slash-palette'}
        >
          <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-2 flex-wrap">
            {(palette.mode === 'audio' ? [['all', 'All'], ...VOICE_EXPRESSION_GROUPS.map(g => [g.id, g.label])] : [['all', 'All'], ['first_task', 'First Task'], ['tools', 'Tools'], ['standard', 'Standard'], ['custom', 'Custom Fields'], ['values', 'Custom Values']]).map(([id, label]) => (
              <button key={id}
                onMouseDown={(e) => { e.preventDefault(); setPalCat(id); }}
                className={`text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors ${palCat === id ? (isDarkMode ? 'bg-white text-black' : 'bg-gray-900 text-white') : `${textSecondary} ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'} border ${cardBorder}`}`}
                data-testid={`${palette.mode === 'audio' ? 'audio-tag-cat' : 'palette-cat'}-${id}`}
                aria-pressed={palCat === id}
              >{label}</button>
            ))}
          </div>
          <div id="prompt-command-list" role="listbox" aria-label={palette.mode === 'audio' ? 'Voice expressions and accents' : 'Fields and tools'} className="min-h-0 overflow-y-auto py-1">
            {paletteItems.length === 0 && (
              <p data-testid="prompt-palette-empty" className={`text-[11px] ${textSecondary} px-4 py-3 text-center`}>No matches{palette.query ? ` for "${palette.mode === 'audio' ? '[' : '/'}${palette.query}"` : ''}</p>
            )}
            {paletteItems.map((item, i) => (
              <button
                key={`${item.kind}-${item.id}`}
                id={`prompt-option-${item.id}`}
                role="option"
                aria-selected={i === palIdx}
                data-palette-active={i === palIdx ? 'true' : 'false'}
                onMouseDown={(e) => { e.preventDefault(); pickItem(item); }}
                onMouseEnter={() => setPalIdx(i)}
                className={`w-full flex items-start gap-3 px-4 py-2 text-left ${i === palIdx ? (isDarkMode ? 'bg-white/[0.07]' : 'bg-gray-100') : ''} ${item.kind === 'tool' && !item.enabled ? 'opacity-60' : ''}`}
                data-testid={`${palette.mode === 'audio' ? 'audio-tag-item' : 'palette-item'}-${item.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[13px] font-mono font-semibold ${item.kind === 'expression' || item.kind === 'block' ? (isDarkMode ? 'text-amber-400' : 'text-amber-700') : item.kind === 'tool' ? (item.enabled ? (isDarkMode ? 'text-violet-400' : 'text-violet-600') : (isDarkMode ? 'text-red-400' : 'text-red-600')) : item.kind === 'value' ? (isDarkMode ? 'text-sky-400' : 'text-sky-600') : (isDarkMode ? 'text-teal-400' : 'text-teal-600')} break-words min-w-0 max-w-full`}>
                      {item.label}
                    </span>
                    {kindBadge(item)}
                  </div>
                  {item.desc && <p className={`text-[11px] ${textSecondary} truncate mt-0.5`}>{item.desc}</p>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      {view !== 'guidelines' && (
      <div className={`shrink-0 flex items-center gap-3 px-4 py-1.5 border-t ${cardBorder} flex-wrap`} data-testid="prompt-legend">
        <span className="flex items-center gap-1 text-[10px]"><span className={`w-2 h-2 rounded-sm ${isDarkMode ? 'bg-violet-400' : 'bg-violet-600'}`} /> <span className={textSecondary}>Tool (on)</span></span>
        <span className="flex items-center gap-1 text-[10px]"><span className={`w-2 h-2 rounded-sm ${isDarkMode ? 'bg-teal-400' : 'bg-teal-600'}`} /> <span className={textSecondary}>Contact variable</span></span>
        <span className="flex items-center gap-1 text-[10px]"><span className={`w-2 h-2 rounded-sm ${isDarkMode ? 'bg-sky-400' : 'bg-sky-600'}`} /> <span className={textSecondary}>Custom value</span></span>
        <span className="flex items-center gap-1 text-[10px]"><span className={`w-2 h-2 rounded-sm ${isDarkMode ? 'bg-red-400' : 'bg-red-600'}`} /> <span className={textSecondary}>Tool off / not in CRM</span></span>
        {!ghlConnected && <span className="flex items-center gap-1 text-[10px]"><Info size={10} className={textSecondary} /> <span className={textSecondary}>CRM not connected — variables shown neutral</span></span>}
      </div>
      )}
    </div>
  );
};

export default PromptEditor;
